package songwritersnotebook.app.database

import songwritersnotebook.app.data.ChordDefinition
import songwritersnotebook.app.data.SavedChord
import songwritersnotebook.app.data.SavedProgression
import songwritersnotebook.app.data.ChordProgression
import songwritersnotebook.platform.getPlatformFileSystem
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.auth.auth
import dev.gitlive.firebase.firestore.firestore
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers

import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import songwritersnotebook.app.AppDispatchers
import com.benasher44.uuid.uuid4

class ChordLibraryRepository {
    private val firestore = Firebase.firestore
    private val auth = Firebase.auth
    private val fileSystem = getPlatformFileSystem()
    
    private val CHORDS_COLLECTION = "saved_chords"
    private val PROGRESSIONS_COLLECTION = "saved_progressions"
    private fun getLocalFileName(userId: String?) = if (userId != null) "favorite_chords_$userId.json" else "favorite_chords_anonymous.json"
    private fun getLocalProgressionsFileName(userId: String?) = if (userId != null) "favorite_progressions_$userId.json" else "favorite_progressions_anonymous.json"

    private val _favoriteChords = MutableStateFlow<List<SavedChord>>(emptyList())
    val favoriteChords: StateFlow<List<SavedChord>> = _favoriteChords.asStateFlow()

    private val _favoriteProgressions = MutableStateFlow<List<SavedProgression>>(emptyList())
    val favoriteProgressions: StateFlow<List<SavedProgression>> = _favoriteProgressions.asStateFlow()

    private val repositoryScope = CoroutineScope(songwritersnotebook.app.AppDispatchers.IO)

    private val json = Json { 
        ignoreUnknownKeys = true 
        encodeDefaults = true
    }

    init {
        // Wait for startSync signal
    }

    fun startSync(userId: String?) {
        repositoryScope.launch {
            loadLocalFavorites(userId)
            loadLocalFavoriteProgressions(userId)
            if (userId == null) return@launch
            
            var retryCount = 0
            val maxRetries = 3
            var success = false
            
            while (retryCount < maxRetries && !success) {
                try {
                    val ownedSnapshot = firestore.collection(CHORDS_COLLECTION).where { "ownerId" equalTo userId }.get()
                    val cloudChords = ownedSnapshot.documents.mapNotNull { try { it.data<SavedChord>() } catch (e: Exception) { null } }
                    _favoriteChords.update { local -> (local + cloudChords).sortedByDescending { it.dateAdded }.distinctBy { it.chord.name + (it.chord.guitarTab?.joinToString(",") ?: "") + (it.chord.pianoNotes?.joinToString(",") ?: "") } }
                    saveLocalFavorites(userId)

                    val ownedProgSnapshot = firestore.collection(PROGRESSIONS_COLLECTION).where { "ownerId" equalTo userId }.get()
                    val cloudProgressions = ownedProgSnapshot.documents.mapNotNull { try { it.data<SavedProgression>() } catch (e: Exception) { null } }
                    _favoriteProgressions.update { local -> (local + cloudProgressions).sortedByDescending { it.dateAdded }.distinctBy { it.progression.id } }
                    saveLocalFavoriteProgressions(userId)
                    
                    success = true
                } catch (e: Exception) { 
                    println("ChordLibraryRepository: Sync failed (Attempt ${retryCount + 1}): $e")
                    retryCount++
                    if (retryCount < maxRetries) {
                        kotlinx.coroutines.delay(1000L * retryCount)
                    }
                }
            }
        }
    }

    private fun loadLocalFavorites(userId: String?) {
        try {
            val fileName = getLocalFileName(userId)
            val file = fileSystem.getFile(fileSystem.getAppFilesDirectory(), fileName)
            if (file.exists()) {
                val content = file.readText()
                if (content.isNotBlank()) _favoriteChords.value = json.decodeFromString(ListSerializer(SavedChord.serializer()), content)
            } else _favoriteChords.value = emptyList()
        } catch (e: Exception) { _favoriteChords.value = emptyList() }
    }

    private fun loadLocalFavoriteProgressions(userId: String?) {
        try {
            val fileName = getLocalProgressionsFileName(userId)
            val file = fileSystem.getFile(fileSystem.getAppFilesDirectory(), fileName)
            if (file.exists()) {
                val content = file.readText()
                if (content.isNotBlank()) _favoriteProgressions.value = json.decodeFromString(ListSerializer(SavedProgression.serializer()), content)
            } else _favoriteProgressions.value = emptyList()
        } catch (e: Exception) { _favoriteProgressions.value = emptyList() }
    }

    private fun saveLocalFavorites(userId: String?) {
        try {
            val fileName = getLocalFileName(userId)
            val file = fileSystem.getFile(fileSystem.getAppFilesDirectory(), fileName)
            file.writeText(json.encodeToString(ListSerializer(SavedChord.serializer()), _favoriteChords.value))
        } catch (e: Exception) {}
    }

    private fun saveLocalFavoriteProgressions(userId: String?) {
        try {
            val fileName = getLocalProgressionsFileName(userId)
            val file = fileSystem.getFile(fileSystem.getAppFilesDirectory(), fileName)
            file.writeText(json.encodeToString(ListSerializer(SavedProgression.serializer()), _favoriteProgressions.value))
        } catch (e: Exception) {}
    }

    suspend fun toggleFavorite(chord: ChordDefinition, tuning: List<String>, isPaidUser: Boolean = false) {
        val userId = auth.currentUser?.uid
        val currentList = _favoriteChords.value
        val existing = currentList.find { it.chord.name == chord.name && it.chord.guitarTab == chord.guitarTab && it.chord.pianoNotes == chord.pianoNotes && it.tuning == tuning }
        if (existing != null) {
            _favoriteChords.value = currentList.filter { it.id != existing.id }
            saveLocalFavorites(userId)
            if (userId != null) deleteChordFromCloud(existing.id)
        } else {
            val savedChord = SavedChord(
                id = com.benasher44.uuid.uuid4().toString(), 
                ownerId = userId ?: "anonymous", 
                chord = chord, 
                tuning = tuning
            )
            _favoriteChords.value = listOf(savedChord) + _favoriteChords.value
            saveLocalFavorites(userId)
            if (userId != null) saveChordToCloud(savedChord)
        }
    }

    private suspend fun saveChordToCloud(savedChord: SavedChord) {
        try {
            firestore.collection(CHORDS_COLLECTION).document(savedChord.id).set(savedChord)
            println("ChordLibraryRepository: Saved ${savedChord.chord.name} to cloud.")
        } catch (e: Exception) {
            println("Error saving chord to cloud: $e")
        }
    }

    private suspend fun deleteChordFromCloud(id: String) {
        try {
            firestore.collection(CHORDS_COLLECTION).document(id).delete()
            println("ChordLibraryRepository: Deleted chord $id from cloud.")
        } catch (e: Exception) {
            println("Error deleting chord from cloud: $e")
        }
    }

    suspend fun toggleFavoriteProgression(progression: ChordProgression) {
        val userId = auth.currentUser?.uid
        val currentList = _favoriteProgressions.value
        val existing = currentList.find { it.progression.id == progression.id }
        if (existing != null) {
            _favoriteProgressions.value = currentList.filter { it.id != existing.id }
            saveLocalFavoriteProgressions(userId)
            if (userId != null) deleteProgressionFromCloud(existing.id)
        } else {
            val savedProg = SavedProgression(
                id = com.benasher44.uuid.uuid4().toString(), 
                ownerId = userId ?: "anonymous", 
                progression = progression.copy(isFavorite = true)
            )
            _favoriteProgressions.value = listOf(savedProg) + _favoriteProgressions.value
            saveLocalFavoriteProgressions(userId)
            if (userId != null) saveProgressionToCloud(savedProg)
        }
    }

    private suspend fun saveProgressionToCloud(savedProgression: SavedProgression) {
        try {
            firestore.collection(PROGRESSIONS_COLLECTION).document(savedProgression.id).set(savedProgression)
            println("ChordLibraryRepository: Saved progression ${savedProgression.progression.name} to cloud.")
        } catch (e: Exception) {
            println("Error saving progression to cloud: $e")
        }
    }

    suspend fun updateFavoriteProgression(progression: ChordProgression) {
        val currentList = _favoriteProgressions.value
        val existing = currentList.find { it.progression.id == progression.id }
        if (existing != null) {
            val updatedProg = existing.copy(progression = progression)
            _favoriteProgressions.value = currentList.map { if (it.id == existing.id) updatedProg else it }
            val userId = auth.currentUser?.uid
            saveLocalFavoriteProgressions(userId)
            if (userId != null) saveProgressionToCloud(updatedProg)
        }
    }

    private suspend fun deleteProgressionFromCloud(id: String) {
        try {
            firestore.collection(PROGRESSIONS_COLLECTION).document(id).delete()
            println("ChordLibraryRepository: Deleted progression $id from cloud.")
        } catch (e: Exception) {
            println("Error deleting progression from cloud: $e")
        }
    }
}
